/**
 * Maps a Shopify AJAX cart object into the canonical PostHog anchor-event
 * properties. Prices are converted cents -> major units (dollars) so they
 * match the checkout web pixel, whose MoneyV2 amounts are already in dollars.
 * Product IDs are stringified to match the pixel's `variant.product.id`.
 */
function phCartProps(cart) {
  cart = cart || {};
  var items = Array.isArray(cart.items) ? cart.items : [];
  var discountApps = Array.isArray(cart.discount_applications)
    ? cart.discount_applications
    : [];
  var codes = discountApps
    .filter(function (d) {
      return d.type === 'discount_code';
    })
    .map(function (d) {
      return d.title;
    });

  return {
    cart_subtotal: (cart.items_subtotal_price || 0) / 100,
    order_value: (cart.total_price || 0) / 100,
    product_ids: items.map(function (i) {
      return String(i.product_id);
    }),
    item_count: cart.item_count || 0,
    discount_value: (cart.total_discount || 0) / 100,
    discount_code: codes.length ? codes.join(', ') : null,
  };
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close(),
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this),
    );
    this.setHeaderCartIconAccessibility();

    this.addEventListener(
      'transitionend',
      (e) => {
        if (e.propertyName != 'transform') return;

        console.log(e);
        var shipping_bar = this.querySelector('shipping-bar');

        if (!shipping_bar) return;

        shipping_bar.dataset.animate = 'true';
      },
      true,
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    const header_wrapper = document.querySelector('.header-wrapper'); //need to delegate as header inject occurs
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    header_wrapper.addEventListener('click', (event) => {
      let cart_link = event.target.closest('#cart-icon-bubble');
      if (cart_link) {
        if (this.dataset.template != 'cart') {
          event.preventDefault();
          this.open(cart_link);
        }
      }
    });

    header_wrapper.addEventListener('keydown', (event) => {
      let cart_link = event.target.closest('#cart-icon-bubble');
      if (event.code.toUpperCase() === 'SPACE' && cart_link) {
        if (this.dataset.template != 'cart') {
          event.preventDefault();
          this.open(cart_link);
        }
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    var trigger = triggeredBy?.closest('#cart-icon-bubble')
      ? 'cart-icon'
      : 'atc_js';

    var cart_json_string = this.querySelector('#cart-items__json');

    var cart_data = cart_json_string
      ? JSON.parse(cart_json_string.innerHTML)
      : {};

    posthog.capture(
      'cart_viewed',
      Object.assign(phCartProps(cart_data), { trigger: trigger }),
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open'),
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, skip_open = false) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    //cart-upsell on /cart page should not open cart, rerender js content
    if (skip_open) return;

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener(
        'click',
        this.close.bind(this),
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
